/**
 * Cult UI Marketing Components
 *
 * Local Cult UI-inspired presentation pieces for marketing pages.
 *
 * Primary responsibilities:
 * - Provide textured buttons and cards for public pages
 * - Keep hover-only reveal and product framing CSS-first
 * - Avoid global motion dependencies and decorative loops
 *
 * Dependencies:
 * - Radix Slot
 * - lib/utils
 */

'use client'

import { ButtonHTMLAttributes, forwardRef, ReactNode, useRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { motion, useMotionValue, useSpring } from 'motion/react'

import { cn } from '@/lib/utils'

type CultTone = 'light' | 'dark'

interface CultTextureButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  tone?: CultTone
}

interface CultTextureCardProps {
  children: ReactNode
  className?: string
  contentClassName?: string
  interactive?: boolean
  tone?: CultTone
}

interface CultShiftCardProps {
  action?: ReactNode
  children?: ReactNode
  className?: string
  description: ReactNode
  eyebrow?: ReactNode
  icon?: ReactNode
  title: ReactNode
}

interface CultScreenFrameProps {
  children: ReactNode
  className?: string
  label?: ReactNode
  title?: ReactNode
}

export const CultTextureButton = forwardRef<HTMLButtonElement, CultTextureButtonProps>(
  (
    {
      asChild = false,
      children,
      className,
      tone = 'dark',
      type,
      onMouseMove,
      onMouseLeave,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    const shellRef = useRef<HTMLDivElement>(null)
    const x = useMotionValue(0)
    const y = useMotionValue(0)
    const springX = useSpring(x, { stiffness: 280, damping: 24 })
    const springY = useSpring(y, { stiffness: 280, damping: 24 })

    return (
      <motion.div
        ref={shellRef}
        className="inline-flex"
        style={{ x: springX, y: springY }}
        onMouseMove={event => {
          const node = shellRef.current
          if (node) {
            const rect = node.getBoundingClientRect()
            x.set((event.clientX - rect.left - rect.width / 2) * 0.14)
            y.set((event.clientY - rect.top - rect.height / 2) * 0.2)
          }
          onMouseMove?.(event as never)
        }}
        onMouseLeave={event => {
          x.set(0)
          y.set(0)
          onMouseLeave?.(event as never)
        }}
      >
        <Comp
          {...props}
          className={cn(
            'group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-full border px-6 text-sm font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_14px_34px_rgba(15,23,42,0.12)] transition-[background-color,border-color,color,box-shadow] duration-200 ease-out before:pointer-events-none before:absolute before:inset-0 before:opacity-75 before:[background-image:radial-gradient(circle_at_30%_0%,rgba(255,255,255,0.24),transparent_36%),linear-gradient(135deg,rgba(255,255,255,0.16)_0_1px,transparent_1px_7px)] motion-reduce:transform-none',
            tone === 'dark'
              ? 'border-slate-950 bg-slate-950 text-white hover:bg-slate-800 dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white'
              : 'border-slate-300 bg-white text-slate-800 hover:border-slate-400 hover:bg-slate-50 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10',
            className
          )}
          ref={ref}
          type={asChild ? undefined : type || 'button'}
        >
          {children}
        </Comp>
      </motion.div>
    )
  }
)
CultTextureButton.displayName = 'CultTextureButton'

// --------------

export function CultTextureCard({
  children,
  className,
  contentClassName,
  interactive = true,
  tone = 'light'
}: CultTextureCardProps) {
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-[1.75rem] border shadow-[0_18px_45px_rgba(15,23,42,0.06)] transition-transform duration-200 ease-out motion-reduce:transition-none',
        interactive && 'hover:-translate-y-0.5',
        tone === 'dark'
          ? 'border-white/10 bg-[linear-gradient(180deg,rgba(16,22,32,0.98),rgba(9,14,22,0.94))] text-slate-50 shadow-[0_18px_45px_rgba(0,0,0,0.3)]'
          : 'border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(245,246,248,0.9))] text-slate-950 dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(16,22,32,0.98),rgba(9,14,22,0.94))] dark:text-slate-50 dark:shadow-[0_18px_45px_rgba(0,0,0,0.3)]',
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.42] [background-image:linear-gradient(135deg,rgba(148,163,184,0.14)_0_1px,transparent_1px_8px)] dark:opacity-[0.12]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(148,163,184,0.5),transparent)] dark:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.24),transparent)]" />
      <div className={cn('relative z-10', contentClassName)}>{children}</div>
    </div>
  )
}

// --------------

export function CultShiftCard({
  action,
  children,
  className,
  description,
  eyebrow,
  icon,
  title
}: CultShiftCardProps) {
  return (
    <CultTextureCard className={cn('h-full p-6', className)}>
      <div className="flex min-h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          {icon ? <div>{icon}</div> : null}
          {eyebrow ? (
            <div className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
              {eyebrow}
            </div>
          ) : null}
        </div>
        <h3 className="mt-5 text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
          {title}
        </h3>
        <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
          {description}
        </p>
        {children || action ? (
          <div className="mt-auto max-h-0 overflow-hidden pt-0 opacity-0 transition-[max-height,opacity,padding-top] duration-200 ease-out group-focus-within:max-h-40 group-focus-within:pt-5 group-focus-within:opacity-100 group-hover:max-h-40 group-hover:pt-5 group-hover:opacity-100 motion-reduce:max-h-none motion-reduce:pt-5 motion-reduce:opacity-100 motion-reduce:transition-none">
            {children}
            {action ? <div className={children ? 'mt-4' : undefined}>{action}</div> : null}
          </div>
        ) : null}
      </div>
    </CultTextureCard>
  )
}

// --------------

export function CultScreenFrame({
  children,
  className,
  label,
  title
}: CultScreenFrameProps) {
  return (
    <CultTextureCard className={cn('overflow-hidden p-3', className)} interactive={false}>
      {title || label ? (
        <div className="mb-3 flex items-center justify-between gap-3 px-2">
          <div className="min-w-0">
            {label ? (
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                {label}
              </p>
            ) : null}
            {title ? (
              <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                {title}
              </p>
            ) : null}
          </div>
          <div className="flex gap-1.5">
            <span className="size-2 rounded-full bg-slate-300 dark:bg-white/20" />
            <span className="size-2 rounded-full bg-slate-300 dark:bg-white/20" />
            <span className="size-2 rounded-full bg-slate-300 dark:bg-white/20" />
          </div>
        </div>
      ) : null}
      <div className="relative overflow-hidden rounded-[1.35rem] border border-slate-200/80 bg-white dark:border-white/10 dark:bg-slate-950">
        {children}
      </div>
    </CultTextureCard>
  )
}
